// Personal data of one crew member (GDPR): export as a ZIP, or erase the PRIVATE data in two steps - "Prepare" shows exactly what goes
// and which vessel records stay exactly as they are (never changed, not even the name), then "Erase personal data" with the name typed.
// Captain / management of the vessel only. Rules: src/lib/crewData.js; server: /api/ai?crewdata=1 (api/_crewData.js) -> sql/final-fix.sql.
import { useState } from 'react'
import Modal from './Modal'
import { nameConfirmed, prepareView } from '../lib/crewData'
import { ERASED_DATA_NOTE } from '../lib/legal'

async function call(body) {
  const r = await fetch('/api/ai?crewdata=1', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
  const d = await r.json().catch(() => ({}))
  if (!r.ok || d.error) throw new Error(d.error || 'Something went wrong (' + r.status + ')')
  return d
}

function List({ title, items, tone, empty }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: tone, textTransform: 'uppercase', letterSpacing: '.04em' }}>{title}</div>
      {items.length ? items.map(x => (
        <div key={x.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
          <span>{x.label}{x.why && <span style={{ color: 'var(--muted)', fontSize: 11.5 }}> · {x.why}</span>}</span>
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{x.count}</span>
        </div>
      )) : <div style={{ fontSize: 13, color: 'var(--muted)', padding: '5px 0' }}>{empty}</div>}
    </div>
  )
}

export default function CrewDataPanel({ crewId, crewName, onClose, onDeleted }) {
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [signed, setSigned] = useState(null)   // the ZIP's 24 h signed link (api/_export.js storeExport)
  const [plan, setPlan] = useState(null)
  const [typed, setTyped] = useState('')
  const [done, setDone] = useState(null)
  const view = prepareView(plan)

  async function run(kind, body, then) {
    setBusy(kind); setError('')
    try { then(await call({ ...body, crew_id: crewId })) } catch (e) { setError(e.message) } finally { setBusy('') }
  }
  const exportZip = () => run('export', { action: 'export' }, d => setSigned(d))
  const prepare = () => run('prepare', { action: 'prepare' }, d => { setPlan(d); setTyped('') })
  const erase = () => run('delete', { action: 'delete', confirm: typed }, d => { setDone(d); onDeleted && onDeleted(crewId) })

  const footer = done
    ? <button className="btn btn-primary" onClick={onClose}>Close</button>
    : plan
    ? <>
        <button className="btn" onClick={() => setPlan(null)} disabled={!!busy}>Back</button>
        <button className="btn" onClick={erase} disabled={!!busy || !nameConfirmed(typed, view.name)}
          style={{ background: nameConfirmed(typed, view.name) ? '#c0392b' : undefined, color: nameConfirmed(typed, view.name) ? '#fff' : undefined, borderColor: '#c0392b' }}>
          {busy === 'delete' ? 'Erasing…' : 'Erase personal data'}
        </button>
      </>
    : <button className="btn" onClick={onClose}>Close</button>

  return (
    <Modal title={'Personal data · ' + (view?.name || crewName || 'crew member')} onClose={onClose} error={error} footer={footer}>
      {done ? (
        <div style={{ fontSize: 13.5 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Personal data erased.</div>
          <div style={{ color: 'var(--muted)', marginTop: 4 }}>
            {done.summary.deleted_rows} records deleted · {done.files.deleted} of {done.summary.files_deleted} files deleted · {done.summary.vessel_records_kept} vessel records kept exactly as they are
          </div>
          {done.files.failed && done.files.failed.length > 0 && <div className="err-box" style={{ marginTop: 10 }}>{done.files.failed.length} file(s) could not be deleted yet - they are retried every day: {done.files.failed.map(f => f.name + ' (' + f.why + ')').join(', ')}</div>}
          <List title="Vessel records - kept exactly as they are" tone="var(--gold2)" items={prepareView(done).kept} empty="None" />
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12 }}>The change history has one entry for this erase, with counts only. {ERASED_DATA_NOTE}</div>
        </div>
      ) : plan ? (
        <div style={{ fontSize: 13.5 }}>
          <div>This is exactly what <b>Erase personal data</b> does. Nothing has changed yet.</div>
          {view.erasedAt && <div className="err-box" style={{ marginTop: 10 }}>Already erased on {String(view.erasedAt).slice(0, 10)}.</div>}
          <List title="Deleted (private data)" tone="#c0392b" items={view.deleted} empty="Nothing" />
          {view.files.length > 0 && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 6 }}>Files deleted from storage ({view.files.length}): {view.files.join(', ')}</div>}
          <List title="Vessel records - kept exactly as they are (name, signatures, signed PDFs)" tone="var(--gold2)" items={view.kept} empty="Nothing" />
          {view.filesKept.length > 0 && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 6 }}>Files kept with those records: {view.filesKept.map(f => f.count + ' ' + f.source).join('; ')}</div>}
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 10 }}>The crew record stays (name, position, dates), archived. The change history gets one entry for this erase, with counts only. {ERASED_DATA_NOTE}</div>
          <div style={{ marginTop: 16, padding: 12, border: '1px solid #c0392b', borderRadius: 10 }}>
            <div style={{ fontWeight: 600 }}>Type <span style={{ userSelect: 'all' }}>{view.name}</span> to confirm. This cannot be undone.</div>
            <input value={typed} onChange={e => setTyped(e.target.value)} placeholder={view.name} autoComplete="off" style={{ marginTop: 8, width: '100%' }} aria-label="Type the crew member's name to confirm" />
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 13.5 }}>
          <div style={{ fontWeight: 600 }}>Export</div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 2 }}>One ZIP with every record of {crewName || 'this crew member'} (JSON + CSV), their files (passport, medical, certificates) and a summary page. The link works for 24 hours.</div>
          <button className="btn" style={{ marginTop: 8 }} onClick={exportZip} disabled={!!busy}>{busy === 'export' ? 'Preparing the ZIP…' : 'Export data (ZIP)'}</button>
          {signed && <div style={{ marginTop: 8, fontSize: 13 }}><a href={signed.url} target="_blank" rel="noopener noreferrer">Download {signed.name}</a>{signed.skipped && signed.skipped.length > 0 && <span style={{ color: 'var(--muted)' }}> · {signed.skipped.length} file(s) not included (see the summary page)</span>}</div>}
          <div style={{ fontWeight: 600, marginTop: 20 }}>Erase personal data</div>
          <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 2 }}>Deletes their private data only: passport, ID, date of birth, medical, contacts, next of kin, personal files, their login. Vessel records (logbooks, Oil Record Book, hours of rest, ISM, permits, certificates, drills, diary) are never changed. You see exactly what happens before anything changes.</div>
          <button className="btn" style={{ marginTop: 8, color: '#c0392b', borderColor: '#c0392b' }} onClick={prepare} disabled={!!busy}>{busy === 'prepare' ? 'Checking…' : 'Prepare erase'}</button>
        </div>
      )}
    </Modal>
  )
}
